import { props } from 'cypress/types/bluebird'
import i18n from 'i18n'
import { t } from 'i18next'
import Logger from 'services/Logger'

export async function shareLink(link: string) {
  const shareData = {
    title: 'Share title',
    text: 'Share text',
    url: link
  }
  if (navigator.share) {
    try {
      Logger.blue('trying to share', link)
      await navigator.share(shareData)
    } catch (e) {
      console.error(e)
    }
  } else {
    console.error('Not supported')
  }
}

export interface ShareableEvent {
  name?: string
  text?: string
  startDate?: string
  endDate?: string
  notes?: Array<{ note: string }>
  note?: string
  location?: string
  allDay?: boolean
}

export class EventShareUtils {
  static buildEventShareText(event: ShareableEvent): string {
    const builder: string[] = []

    // Empty line
    builder.push('')

    // Separator line
    builder.push('----------')

    // Title - use either name or text property
    const title = event.name || event.text || 'Untitled Event'
    builder.push(title)

    if (event?.location) {
      builder.push(event.location)
    }

    // Start date/time
    if (event.startDate && event.startDate !== 'null') {
      const startDateTime = this.formatDateTime(event.startDate, event.allDay)
      if (startDateTime) {
        builder.push(`${startDateTime}`)
      }
    }

    // End date/time
    if (
      event.endDate &&
      event.endDate !== 'null' &&
      event.endDate !== event.startDate
    ) {
      const endDateTime = this.formatDateTime(event.endDate, event.allDay)
      if (endDateTime) {
        builder.push(`${endDateTime}`)
      }
    }

    // Notes - combine both note field and notes array (similar to notesAsText())
    const notesText = this.buildNotesText(event)
    if (notesText) {
      builder.push(`${t('notes')} \n${notesText}`)
    }

    return builder.join('\n')
  }

  private static buildNotesText(event: ShareableEvent): string | null {
    const parts: string[] = []

    // Add single note field if exists
    if (event.note && event.note.trim() && event.note !== 'null') {
      parts.push(event.note)
    }

    // Add notes array if exists (similar to your Android notesAsText method)
    if (event.notes && event.notes.length > 0) {
      for (const noteItem of event.notes) {
        if (noteItem.note && noteItem.note.trim()) {
          parts.push(`- ${noteItem.note}`)
        }
      }
    }

    return parts.length > 0 ? parts.join('\n') : null
  }

  private static formatDateTime(
    dateString: string,
    allday?: boolean
  ): string | null {
    try {
      // Handle timestamp strings (like your Android startDate/endDate)
      const timestamp = parseInt(dateString)
      const date = new Date(timestamp * 1000)

      // Check if it's a valid date
      if (isNaN(date.getTime())) {
        return null
      }

      if (allday) {
        return `${date.toLocaleDateString(i18n.language, {
          month: 'short',
          day: 'numeric'
        })}, ${t('allDay')}`
      }

      return date.toLocaleDateString(i18n.language, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      })
    } catch (error) {
      console.error('Error formatting date:', error)
      return null
    }
  }
}
